/**
 * Splat swaps in captured video (beginFrameCapture and the editor's Render Video): every frame
 * shows the swaps the published scene would show for that frame's camera pose and tour
 * position, and nothing depends on wall-clock time or network speed.
 *
 * For each frame, before it renders:
 * 1. Swap transitions already running (dissolve, wipe) advance by the video time since the
 *    previous frame, so a transition spans as many frames as it lasts seconds live.
 * 2. Proximity areas are evaluated at the frame's pose and, when the frame is on the tour
 *    (it has a tour progress), chain swaps are applied for that progress, as the live tour
 *    would apply them.
 * 3. The frame waits for every swap load this started (and the skyboxes it applied), and for
 *    a splat swapped in to be sorted and streamed for the pose, so a swap starts on the frame
 *    where the camera crossed its trigger, however slow the network or the machine.
 *
 * The first frame cuts straight to its state instead of transitioning into it: a video starts
 * on the scene as configured, not on a dissolve away from whatever the viewer showed before.
 *
 * The engine work lives in createViewer (CaptureSwapHost); this keeps the frame order
 * unit-testable.
 */
import type { CameraPose } from '../types';

export interface CaptureSwapFrame {
    /** The frame's camera pose (PlayCanvas world coordinates). */
    pose: CameraPose;
    /**
     * Tour timeline progress of the frame (as passed to getPathPoseAtProgress), or undefined
     * when the frame is not on the tour (a custom path): then only proximity areas apply.
     */
    progress?: number;
    /** Video time since the previous frame, in seconds (1 / fps). */
    dtSec: number;
}

export interface CaptureSwapHost {
    /** Advance running swap transitions by `dtSec` of video time. */
    advanceTransitions(dtSec: number): void;
    /** Jump running swap transitions to their end. */
    finishTransitions(): void;
    /** Swaps cut instead of transitioning until the returned function is called. */
    suspendTransitions(): () => void;
    /** Evaluate proximity areas at the pose, and chain swaps at the progress (null: off the tour). */
    applySwaps(pose: CameraPose, progress: number | null): void;
    /**
     * Resolves once no swap load (or skybox load) started so far is still running and a splat
     * swapped in this frame is ready to render at `pose`.
     */
    settle(pose: CameraPose): Promise<void>;
}

export class CaptureSwapDriver {
    private frames = 0;
    private busy = false;

    constructor(private readonly host: CaptureSwapHost) {}

    /** Frames prepared so far. */
    get frameCount(): number {
        return this.frames;
    }

    /** Bring the swaps up to date for one frame; resolves when the frame may render. */
    async prepareFrame(frame: CaptureSwapFrame): Promise<void> {
        if (this.busy) throw new Error('[StorySplat Viewer] The previous capture frame is still being prepared; await it first.');
        this.busy = true;
        const first = this.frames === 0;
        this.frames++;
        let resume: (() => void) | null = null;
        try {
            if (first) {
                this.host.finishTransitions();
                resume = this.host.suspendTransitions();
            } else {
                const dt = Number.isFinite(frame.dtSec) && frame.dtSec > 0 ? frame.dtSec : 0;
                if (dt > 0) this.host.advanceTransitions(dt);
            }
            const progress = typeof frame.progress === 'number' && Number.isFinite(frame.progress) ? frame.progress : null;
            this.host.applySwaps(frame.pose, progress);
            await this.host.settle(frame.pose);
        } finally {
            resume?.();
            this.busy = false;
        }
    }
}

/** Frame time for a capture at `fps` frames per second (30 when not given or invalid). */
export function captureFrameDt(fps: number | undefined): number {
    return typeof fps === 'number' && Number.isFinite(fps) && fps > 0 ? 1 / fps : 1 / DEFAULT_CAPTURE_FPS;
}

export const DEFAULT_CAPTURE_FPS = 30;
